<template>
  <div
    class="s-company-card"
    :class="[
      $attrs.class,
      {
        's-company-card--logo-emphasized': logoEmphasized,
        's-company-card--logo-mobile-forced': mobileForced,
      },
    ]"
    :style="$attrs.style"
  >
    <div class="s-company-card__logo">
      <img
        v-if="logo"
        v-show="!imageFailed"
        ref="logoRef"
        :alt="name"
        class="s-company-card__logo__image"
        loading="lazy"
        :src="logo"
      />
      <i
        v-if="!logo || imageFailed"
        class="s-company-card__logo__image--no-logo__icon fa-regular fa-building"
      />
    </div>
    <h1 v-if="logoEmphasized" class="s-company-card__company-name">
      {{ name }}
    </h1>
    <p v-else class="s-company-card__company-name">{{ name }}</p>
    <div v-if="hasBadges" class="s-company-card__badges">
      <SBadgeTopPro v-if="isTopPro" />
      <SBadgeNewPro v-if="isNewPro" />
      <SBadgePremiumPartner v-if="isPremiumPartner" />
      <SBadgeTimesChosen
        v-if="timesChosen && timesChosen > 1"
        :times-chosen="timesChosen"
      />
    </div>
    <div
      v-if="reviews && reviews.total > 0"
      class="s-company-card__review-score"
    >
      <SReviewScoreAtom :model-value="reviews.average" />
      <a
        v-if="reviewsUrl"
        class="s-company-card__review-score__amount s-company-card__review-score__amount--link"
        :href="reviewsUrl"
      >
        ({{ reviews.total }} {{ tr("client.company_card.reviews") }})
      </a>
      <SButton
        v-else-if="reviewCountClickable"
        action-type="link"
        class="s-company-card__review-score__amount"
        color="gray"
        size="inline"
        @click="emit('reviewCountClicked')"
      >
        ({{ reviews.total }} {{ tr("client.company_card.reviews") }})
      </SButton>
      <span v-else class="s-company-card__review-score__amount">
        ({{ reviews.total }} {{ tr("client.company_card.reviews") }})
      </span>
    </div>
    <p v-else class="s-company-card__no-reviews">
      {{ tr(`client.company_card.no_reviews`) }}
    </p>
    <div v-if="city" class="s-company-card__location">
      <i class="fa-regular fa-location-dot" />
      {{ city }}
      <span
        v-if="hasActiveCampaignForMunicipality && distance && distance > 20"
      >
        ({{ tr("client.company_card.active_in_region") }})
      </span>
      <span v-else-if="distance">({{ distance }} km)</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from "vue";

import { SButton, SReviewScoreAtom } from "@solvari/common-fe";
import { useImageFailed } from "@solvari/common-fe/composables";
import { useI18nCompanyCard } from "@solvari/translations";

import SBadgeNewPro from "@/other/pro/s-company-badges/SBadgeNewPro.vue";
import SBadgePremiumPartner from "@/other/pro/s-company-badges/SBadgePremiumPartner.vue";
import SBadgeTimesChosen from "@/other/pro/s-company-badges/SBadgeTimesChosen.vue";
import SBadgeTopPro from "@/other/pro/s-company-badges/SBadgeTopPro.vue";

defineOptions({ inheritAttrs: false });

const { tr } = useI18nCompanyCard();

const props = withDefaults(
  defineProps<{
    city?: string | null;
    distance?: number | null;
    hasActiveCampaignForMunicipality?: boolean;
    isNewPro?: boolean;
    isPremiumPartner?: boolean;
    isTopPro?: boolean;
    logo?: string | null;
    logoEmphasized?: boolean;
    mobileForced?: boolean;
    name: string;
    reviewCountClickable?: boolean;
    reviews?: App.Resources.Customer.CompanyCardReviewsResource | null;
    reviewsUrl?: string;
    timesChosen?: number | null;
  }>(),
  {
    city: null,
    distance: null,
    hasActiveCampaignForMunicipality: false,
    isNewPro: false,
    isPremiumPartner: false,
    isTopPro: false,
    logo: null,
    logoEmphasized: false,
    mobileForced: false,
    reviews: null,
    timesChosen: null,
  },
);

const emit = defineEmits<{ reviewCountClicked: [] }>();

const imageEl = useTemplateRef<HTMLImageElement>("logoRef");
const imageFailed = useImageFailed(imageEl);

const hasBadges = computed(() => {
  return (
    props.isTopPro ||
    props.isPremiumPartner ||
    props.timesChosen ||
    props.isNewPro
  );
});
</script>

<style lang="postcss">
.s-company-card {
  @apply grid items-center;
  grid-template-areas:
    "logo company-name"
    "badges badges"
    "review-score review-score"
    "location location";
  grid-template-columns: max-content 1fr;

  &--logo-emphasized {
    grid-template-areas:
      "logo logo"
      "company-name company-name"
      "badges badges"
      "review-score review-score"
      "location location";
    .s-company-card {
      &__logo,
      &__logo__image,
      &__logo__image--no-logo {
        @apply h-24 w-24;
      }

      &__logo__image--no-logo__icon {
        @apply h-14 w-14;
      }

      @screen 600 {
        &__company-name {
          @apply text-4xl;
        }
      }
    }
  }

  &--logo-mobile-forced {
    @apply items-start justify-start space-y-3 p-4 gap-x-3;
    grid-template-areas:
      "logo company-name"
      "badges badges"
      "review-score review-score"
      "location location";
    .s-company-card {
      &__logo,
      &__logo__image,
      &__logo__image--no-logo {
        @apply m-0 size-10;
      }

      &__logo__image--no-logo__icon {
        @apply size-4;
      }

      &__company-name {
        @apply !my-auto line-clamp-2 text-base font-bold text-slate-700;
      }

      &__review-score {
        &__amount {
          @apply text-sm;
        }
      }

      &__location {
        @apply text-sm font-bold text-slate-700;
        & span {
          @apply text-sm font-normal text-slate-500;
        }
      }

      &__badges {
        .s-company-badge {
          @apply h-min;
        }
      }
    }
  }

  &__logo {
    @apply mb-2 mr-2 flex h-10 w-10 items-center overflow-hidden rounded-lg border object-contain;
    grid-area: logo;

    &__image {
      @apply h-10 w-10 object-contain;

      &--no-logo__icon {
        @apply mx-auto h-6 w-6 text-gray-400;
      }
    }
  }
  &__company-name {
    @apply mb-2 overflow-hidden text-ellipsis text-lg font-semibold text-gray-800;
    grid-area: company-name;
  }
  &__badges {
    @apply mb-2 flex flex-wrap gap-2;
    grid-area: badges;
  }
  &__review-score {
    @apply mb-2 flex flex-wrap items-center gap-1;
    grid-area: review-score;

    &__amount {
      @apply text-gray-400;

      &--link {
        @apply text-primary-700;
        &:hover {
          @apply text-primary-600;
        }
      }
    }
    .s-review-score-atom__star--filled {
      @apply text-warning-300;
    }
  }
  &__location {
    @apply mb-2 flex items-center text-gray-600 gap-2;
    grid-area: location;
  }
  &__no-reviews {
    @apply mb-2 text-gray-400;
    grid-area: review-score;
  }
}

@screen 600 {
  .s-company-card {
    @apply gap-x-3;
    grid-template-areas:
      "logo company-name"
      "logo badges"
      "logo review-score"
      "logo location";

    .s-company-card__logo {
      @apply mb-auto h-24 w-24;
      &__image {
        @apply h-full w-full;

        &--no-logo__icon {
          @apply h-14 w-14;
        }
      }
    }

    &--logo-mobile-forced {
      @apply h-full max-h-60 items-start justify-start space-y-3 p-4;
      grid-template-areas:
        "logo company-name"
        "badges badges"
        "review-score review-score"
        "location location";
      grid-template-rows:
        min-content /* logo */
        auto /* badges */
        min-content /* review-score */
        min-content; /* location */
      .s-company-card {
        &__logo,
        &__logo__image,
        &__logo__image--no-logo {
          @apply m-0 size-10;
        }

        &__logo__image--no-logo__icon {
          @apply size-4;
        }

        &__badges {
          @apply text-xs font-bold text-slate-700;
        }

        &__company-name {
          @apply my-auto line-clamp-2 text-base font-bold text-slate-700;
        }
      }

      &__logo {
        @apply m-auto h-24 w-24;
        &__image {
          @apply h-full w-full;

          &--no-logo__icon {
            @apply h-14 w-14;
          }
        }
      }
    }

    &__company-name {
      @apply text-xl;
    }
  }
}
</style>
